<template>
  <div class="tutorial-page">
    <section v-if="!readonlyMode" class="timer-bar">
      <div>
        <strong>{{ timerText }}</strong>
        <span>{{ timerHint }}</span>
      </div>
      <button type="button" @click="toggleTimer">{{ timerButtonText }}</button>
    </section>

    <section class="tutorial-frame">
      <van-loading v-if="frameLoading" class="frame-loading" color="#2F8F6B">教程加载中...</van-loading>
      <iframe
        :src="tutorialUrl"
        :title="`${massagePlanTitle}教程`"
        @load="frameLoading = false"
      />
    </section>

    <div v-if="!readonlyMode" class="bottom-action">
      <van-button block color="#2F8F6B" :disabled="elapsedSeconds <= 0" :loading="submitting" @click="completeMassage">
        完成本次{{ massagePlanTitle }}
      </van-button>
    </div>
  </div>
</template>

<script>
import { resolveTcmStudyToken, startMassageTimer, submitMassageComplete } from '@/api/tcmStudy';

const DEFAULT_TUTORIAL_URL = 'https://yboss.yubinglabs.com/discuz/news/2026-04-21/c5aHLWJ6Pnj.html';
const PLAN_2_TUTORIAL_URL = 'https://yboss.yubinglabs.com/discuz/news/2026-07-03/36cEcYvh1E6.html';

function pad (value) {
    const text = String(value);
    return text.length >= 2 ? text : `0${text}`;
}

export default {
    name: 'TcmMassageTutorial',
    data () {
        return {
            token: '',
            frameLoading: true,
            running: false,
            elapsedSeconds: 0,
            timerBaseSeconds: 0,
            timerRunStartedAt: 0,
            timer: null,
            submitting: false
        };
    },
    computed: {
        tutorialUrl () {
            if (Number(this.$route.query.massage_plan_no) === 2) {
                return PLAN_2_TUTORIAL_URL;
            }
            return this.$route.query.tutorial_url || DEFAULT_TUTORIAL_URL;
        },
        massagePlanTitle () {
            return this.$route.query.massage_plan_title || '小儿推拿方案1';
        },
        readonlyMode () {
            return this.$route.query.readonly === '1' || this.$route.query.mode === 'view';
        },
        timerText () {
            const minutes = Math.floor(this.elapsedSeconds / 60);
            const seconds = this.elapsedSeconds % 60;
            return `${pad(minutes)}:${pad(seconds)}`;
        },
        timerHint () {
            if (this.elapsedSeconds >= 15 * 60) {
                return '已超过建议时长，请注意宝宝状态';
            }
            if (this.elapsedSeconds >= 10 * 60) {
                return '已达到建议时长';
            }
            return '建议 10-15 分钟';
        },
        timerButtonText () {
            if (!this.elapsedSeconds && !this.running) {
                return '开始';
            }
            return this.running ? '暂停' : '继续';
        }
    },
    mounted () {
        this.token = resolveTcmStudyToken(this.$route);
    },
    beforeDestroy () {
        this.stopTimer();
    },
    methods: {
        returnToTaskPage (replace = false) {
            const query = {
                ...this.$route.query,
                date: this.$route.query.record_date || this.$route.query.date
            };
            delete query.return_path;
            const target = {
                path: this.$route.query.return_path || '/tcm-study/home',
                query
            };
            if (replace) {
                this.$router.replace(target);
            } else {
                this.$router.push(target);
            }
        },
        payload () {
            return {
                baby_id: this.$route.query.baby_id,
                subject_id: this.$route.query.subject_id || undefined,
                record_date: this.$route.query.record_date,
                task_key: this.$route.query.task_key || 'massage',
                record_id: this.$route.query.record_id || undefined
            };
        },
        async toggleTimer () {
            if (this.readonlyMode) {
                return;
            }
            if (this.running) {
                this.stopTimer();
                return;
            }
            if (!this.elapsedSeconds) {
                try {
                    const result = await startMassageTimer(this.token, this.payload());
                    const startedAt = result && result.started_at ? new Date(result.started_at.replace(/-/g, '/')).getTime() : 0;
                    const serverTime = result && result.server_time ? new Date(result.server_time.replace(/-/g, '/')).getTime() : 0;
                    if (startedAt && serverTime && serverTime >= startedAt) {
                        this.elapsedSeconds = Math.floor((serverTime - startedAt) / 1000);
                    }
                } catch (error) {
                    this.$toast(error.message || '开始失败');
                    return;
                }
            }
            this.running = true;
            this.timerBaseSeconds = this.elapsedSeconds;
            this.timerRunStartedAt = Date.now();
            this.timer = window.setInterval(() => {
                this.syncElapsedSeconds();
            }, 1000);
        },
        syncElapsedSeconds () {
            if (!this.running || !this.timerRunStartedAt) {
                return;
            }
            this.elapsedSeconds = this.timerBaseSeconds + Math.floor((Date.now() - this.timerRunStartedAt) / 1000);
        },
        stopTimer () {
            this.syncElapsedSeconds();
            if (this.timer) {
                window.clearInterval(this.timer);
                this.timer = null;
            }
            this.running = false;
            this.timerRunStartedAt = 0;
        },
        completeMassage () {
            if (this.readonlyMode) {
                return;
            }
            if (this.elapsedSeconds < 60) {
                this.$dialog.confirm({
                    title: `确认完成本次${this.massagePlanTitle}？`,
                    message: '当前计时时间较短，请确认是否已完成操作。',
                    confirmButtonText: '确认完成',
                    cancelButtonText: '继续操作'
                }).then(() => this.submitComplete()).catch(() => {});
                return;
            }
            this.submitComplete();
        },
        async submitComplete () {
            if (this.submitting) {
                return;
            }
            this.stopTimer();
            this.submitting = true;
            try {
                const result = await submitMassageComplete(this.token, {
                    ...this.payload(),
                    duration_seconds: this.elapsedSeconds
                });
                this.$router.replace({
                    path: '/tcm-study/massage/feedback',
                    query: {
                        ...this.$route.query,
                        completed_count: result.completed_count || 1,
                        duration_seconds: this.elapsedSeconds,
                        return_path: this.$route.query.return_path || '/tcm-study/home'
                    }
                });
            } catch (error) {
                this.$toast(error.message || '提交失败');
            } finally {
                this.submitting = false;
            }
        }
    }
};
</script>

<style lang="sass" scoped>
.tutorial-page
    height: 100vh
    height: 100dvh
    display: flex
    flex-direction: column
    background: #ffffff
    color: #1f2329
    padding-top: env(safe-area-inset-top)
    box-sizing: border-box

.timer-bar
    flex: none
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
    padding: 12px 16px
    border-bottom: 1px solid #e5e6eb
    background: #ffffff

.timer-bar strong
    display: block
    font-size: 30px
    line-height: 36px
    font-weight: 700

.timer-bar span
    display: block
    margin-top: 2px
    color: #646a73
    font-size: 13px
    line-height: 18px

.timer-bar button
    min-width: 72px
    height: 36px
    border: 0
    border-radius: 18px
    background: #eaf6f1
    color: #2f8f6b
    font-size: 15px
    font-weight: 600

.tutorial-frame
    position: relative
    flex: 1
    min-height: 0

.tutorial-frame iframe
    width: 100%
    height: 100%
    border: 0
    display: block

.frame-loading
    position: absolute
    top: 40%
    left: 0
    right: 0
    justify-content: center

.bottom-action
    flex: none
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom))
    background: #ffffff
    box-shadow: 0 -2px 8px rgba(0, 0, 0, .04)
    box-sizing: border-box
</style>
